import { useQuery } from '@tanstack/react-query';
import { api } from './api-client';
import type { InboxCategory, InboxFiltersState, InboxPage } from './types';

// Derives the `from` (ISO-8601) lower bound from a date-range filter value. Exported so the folder
// counts hook derives it through the SAME code path as the list, guaranteeing the rail counts and
// the visible list always agree on the date filter.
export function dateRangeToFrom(range: string): string | null {
  const now = new Date();
  if (range === 'last7') {
    const d = new Date(now.getTime() - 7 * 86_400_000);
    return d.toISOString();
  }
  if (range === 'last30') {
    const d = new Date(now.getTime() - 30 * 86_400_000);
    return d.toISOString();
  }
  if (range === 'last90') {
    const d = new Date(now.getTime() - 90 * 86_400_000);
    return d.toISOString();
  }
  return null;
}

export function useInboxFolder(
  folder: string,
  category: InboxCategory | null,
  filters: InboxFiltersState,
  page = 0,
) {
  const from = dateRangeToFrom(filters.dateRange);
  // Trim q before it enters the query key (and the request) so "a" and "a " share one cache entry.
  const q = filters.q.trim();
  return useQuery({
    queryKey: ['inbox', 'folder', folder, category, q, filters.dateRange, page],
    queryFn: () => {
      const params = new URLSearchParams({ folder, page: String(page) });
      if (category) params.set('type', category);
      if (from) params.set('from', from);
      if (q) params.set('q', q);
      return api.get<InboxPage>(`/v1/inbox?${params.toString()}`);
    },
    // Poll on the same cadence as the unread badge and rail counts (useInboxCounts): without this,
    // a notification arriving while the user sits on /inbox bumps the counts but never renders in
    // the visible list until a manual refresh (refetchOnWindowFocus is disabled globally).
    refetchInterval: 30_000,
  });
}
